// src/pages/DealDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { dealAPI, userAPI, reviewAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  FiHeart,
  FiMapPin,
  FiClock,
  FiShoppingCart,
  FiStar,
  FiPhone,
  FiMail,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiLock
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import Loading from "../components/common/Loading";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import config from "../config";
import { getAllDealImageUrls } from "../utils/dealImageHelper";

const currencyFmt = (value) =>
  typeof value === "number"
    ? value.toLocaleString("fr-FR") + " MAD"
    : value
    ? `${value} MAD`
    : "0 MAD";

const safeGet = (v, fallback = null) => (v === undefined || v === null ? fallback : v);

const DealDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const auth = useAuth();
  const user = auth?.user ?? null;

  const cart = useCart();
  const addToCart = cart?.addToCart ?? null;

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const [isFavorite, setIsFavorite] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadDeal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dealAPI.getOne(slug);
      const payload = res?.data?.data ?? res?.data ?? res ?? null;

      if (!mountedRef.current) return;

      if (!payload) {
        toast.error("Deal non trouvé");
        navigate("/deals");
        return;
      }

      const imageUrls = getAllDealImageUrls(payload, "/placeholder.png");

      const normalized = {
        ...payload,
        imageUrls,
        locations: Array.isArray(payload.locations)
          ? payload.locations
          : payload.location
          ? [payload.location]
          : [],
        highlights: Array.isArray(payload.highlights)
          ? payload.highlights
          : payload.highlights
          ? [payload.highlights]
          : [],
        conditions: Array.isArray(payload.conditions)
          ? payload.conditions
          : payload.conditions
          ? [payload.conditions]
          : [],
        discountedPrice: Number(payload.discountedPrice ?? payload.price ?? payload.discounted_price ?? 0),
        originalPrice: Number(payload.originalPrice ?? payload.original_price ?? payload.priceBefore ?? 0),
        rating: Number(payload.rating ?? 0),
        reviewCount: Number(payload.reviewCount ?? payload.reviewsCount ?? payload.reviews ?? 0),
        quantityRemaining: Number(payload.quantityRemaining ?? payload.remaining ?? payload.stock ?? 0),
        startDate: payload.startDate ?? payload.validFrom ?? payload.startDateISO,
        endDate: payload.endDate ?? payload.validUntil ?? payload.endDateISO
      };

      setDeal(normalized);
      setSelectedImage(0);
      setQuantity(1);
      setReviewRating(normalized.rating || 0);
      setReviewCount(normalized.reviewCount || 0);
    } catch (err) {
      console.error("loadDeal error", err);
      toast.error("Deal non trouvé");
      navigate("/deals");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [slug, navigate]);

  const incrementViews = useCallback(async () => {
    try {
      if (dealAPI && typeof dealAPI.incrementViews === "function") {
        await dealAPI.incrementViews(slug);
      }
    } catch (error) {
      console.error("Erreur incrémentation vues:", error);
    }
  }, [slug]);

  useEffect(() => {
    (async () => {
      await loadDeal();
      await incrementViews();
    })();
  }, [loadDeal, incrementViews]);

  useEffect(() => {
    let cancelled = false;

    const checkFavorite = async () => {
      if (!user || !deal) {
        setIsFavorite(false);
        return;
      }
      try {
        const res = await userAPI.getFavorites();
        const favs = res?.data?.data ?? res?.data ?? res ?? [];
        const arr = Array.isArray(favs) ? favs : favs.favorites ?? favs.items ?? [];
        if (cancelled) return;

        const dealId = deal._id || deal.id;
        const isFav = Array.isArray(arr) && arr.some((fav) => (fav?._id || fav?.id) === dealId);
        setIsFavorite(Boolean(isFav));
      } catch (error) {
        console.error("Erreur vérification favoris:", error);
      }
    };

    checkFavorite();
    return () => {
      cancelled = true;
    };
  }, [user, deal]);

  const loadReviews = useCallback(
    async (dealId) => {
      if (!dealId) return;
      setReviewsLoading(true);
      try {
        const res = await reviewAPI.getByDeal(dealId);
        const payload = res?.data?.data ?? res?.data ?? [];
        const arr = Array.isArray(payload) ? payload : [];
        setReviews(arr);

        const count = arr.length;
        if (count > 0) {
          const sum = arr.reduce((acc, r) => acc + (Number(r?.rating) || 0), 0);
          const avg = sum / count;
          setReviewRating(Number(avg.toFixed(1)));
          setReviewCount(count);
        } else {
          setReviewRating(deal?.rating || 0);
          setReviewCount(deal?.reviewCount || 0);
        }
      } catch (e) {
        console.error("loadReviews error", e);
        setReviews([]);
        setReviewRating(deal?.rating || 0);
        setReviewCount(deal?.reviewCount || 0);
      } finally {
        setReviewsLoading(false);
      }
    },
    [deal]
  );

  useEffect(() => {
    if (deal && (deal._id || deal.id)) loadReviews(deal._id || deal.id);
  }, [deal, loadReviews]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.info("Connectez-vous pour laisser un avis");
      navigate("/login");
      return;
    }
    if (!deal || (!deal._id && !deal.id)) {
      toast.error("Deal introuvable");
      return;
    }
    if (!myRating || myRating < 1 || myRating > 5) {
      toast.error("Choisissez une note entre 1 et 5");
      return;
    }
    if (!myComment.trim()) {
      toast.error("Le commentaire est obligatoire");
      return;
    }

    try {
      await reviewAPI.create({
        dealId: deal._id || deal.id,
        rating: myRating,
        comment: myComment
      });
      toast.success("Avis envoyé");
      setMyRating(0);
      setMyComment("");
      await loadReviews(deal._id || deal.id);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erreur lors de l'envoi de votre avis";
      toast.error(msg);
    }
  };

  const renderStarsInput = () => (
    <div className="flex items-center space-x-1 mb-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button key={star} type="button" onClick={() => setMyRating(star)} className="focus:outline-none">
          <FiStar className={star <= myRating ? "text-yellow-400 fill-current" : "text-gray-300"} size={22} />
        </button>
      ))}
    </div>
  );

  const renderStarsReadOnly = (rating) => {
    const r = Number(rating) || 0;
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar key={star} className={star <= r ? "text-yellow-400 fill-current" : "text-gray-300"} size={14} />
        ))}
      </div>
    );
  };

  if (loading) return <Loading />;
  if (!deal) return null;

  const discountPercentage = safeGet(
    deal.discountPercentage,
    deal.originalPrice > 0
      ? Math.round(((deal.originalPrice - deal.discountedPrice) / deal.originalPrice) * 100)
      : 0
  );

  const daysRemaining = (() => {
    const end = deal.endDate || deal.endDateISO || deal.validUntil || deal.end;
    if (!end) return 0;
    const diffMs = new Date(end).getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, Number.isFinite(days) ? days : 0);
  })();

  const images = Array.isArray(deal.imageUrls) && deal.imageUrls.length ? deal.imageUrls : ["/placeholder.png"];

  const locations =
    Array.isArray(deal.locations) && deal.locations.length ? deal.locations : deal.location ? [deal.location] : [];

  const toggleFavorite = async () => {
    if (!user) {
      toast.info("Connectez-vous pour gérer vos favoris");
      navigate("/login");
      return;
    }
    if (!deal || (!deal._id && !deal.id)) return;

    try {
      const dealId = deal._id || deal.id;

      if (typeof userAPI.toggleFavorite === "function") {
        await userAPI.toggleFavorite(dealId);
      } else if (isFavorite && typeof userAPI.removeFavorite === "function") {
        await userAPI.removeFavorite(dealId);
      } else if (!isFavorite && typeof userAPI.addFavorite === "function") {
        await userAPI.addFavorite(dealId);
      }

      const newValue = !isFavorite;
      setIsFavorite(newValue);
      toast.success(newValue ? "Ajouté aux favoris" : "Retiré de vos favoris");
    } catch (error) {
      console.error("toggleFavorite error", error);
      toast.error("Impossible de mettre à jour les favoris");
    }
  };

  const goPrevImage = () => setSelectedImage((c) => (c === 0 ? images.length - 1 : c - 1));
  const goNextImage = () => setSelectedImage((c) => (c === images.length - 1 ? 0 : c + 1));

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => setIsLightboxOpen(false);

  const handleAddToCart = () => {
    if (!deal) return;

    if (!addToCart) {
      toast.error("Le panier est indisponible");
      return false;
    }

    if ((deal.quantityRemaining ?? 0) === 0) {
      toast.error("Ce deal est épuisé");
      return false;
    }

    const id = deal._id || deal.id || deal.slug;
    if (!id) {
      toast.error("Deal invalide");
      return false;
    }

    const item = {
      dealId: id,
      _id: id,
      title: deal.title,
      price: Number(deal.discountedPrice ?? deal.price ?? 0),
      image: images[0] || "/placeholder.png",
      slug
    };

    try {
      addToCart(item, quantity);
      toast.success("Ajouté au panier");
      return true;
    } catch (e) {
      console.error("handleAddToCart error", e);
      toast.error("Erreur lors de l'ajout au panier");
      return false;
    }
  };

  const handleBuyNow = () => {
    const ok = handleAddToCart();
    if (ok) navigate("/checkout");
  };

  const companyName = config.SUPPORT_NAME || "—";
  const companyAddress = config.SUPPORT_ADDRESS || "";
  const companyPhone = config.SUPPORT_PHONE || "";
  const companyEmail = config.SUPPORT_EMAIL || "";
  const companyWhatsapp = config.SUPPORT_WHATSAPP_CLEAN || "";

  const whatsappText = encodeURIComponent(`Bonjour, je suis intéressé par: ${deal.title}`);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* IMAGE SECTION */}
          <div>
            <div className="relative rounded-xl overflow-hidden border bg-white">
              <img
                src={images[selectedImage]}
                alt={deal.title || "Deal"}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/placeholder.png";
                }}
                className="w-full h-[340px] md:h-[420px] object-cover cursor-zoom-in"
                onClick={() => openLightbox(selectedImage)}
              />

              {discountPercentage > 0 && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1.5 rounded-lg font-bold text-sm">
                  -{discountPercentage}%
                </div>
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={goPrevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-2 shadow"
                    aria-label="Image précédente"
                  >
                    <FiChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={goNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 hover:bg-white rounded-full p-2 shadow"
                    aria-label="Image suivante"
                  >
                    <FiChevronRight size={22} />
                  </button>
                </>
              )}
            </div>

            <div className="mt-3 flex gap-3">
              {images.slice(0, 4).map((img, index) => (
                <button
                  key={String(index)}
                  type="button"
                  onClick={() => setSelectedImage(index)}
                  className={`h-16 w-24 rounded-lg overflow-hidden border ${
                    selectedImage === index ? "ring-2 ring-primary-500 border-primary-500" : "border-gray-200"
                  }`}
                  aria-label={`Voir image ${index + 1}`}
                >
                  <img
                    src={img}
                    alt={`${deal.title || ""} ${index + 1}`}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder.png";
                    }}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* PRICE & ACTIONS SECTION */}
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight">{deal.title}</h1>

            <div className="mt-2 flex items-center gap-3">
              <div className="flex items-center gap-1 text-sm">
                <FiStar className="text-yellow-400" />
                <span className="font-semibold text-gray-900">{reviewRating ?? 0}</span>
                <span className="text-gray-500">({reviewCount ?? 0} avis)</span>
              </div>

              <button
                type="button"
                onClick={toggleFavorite}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-red-500"
                aria-pressed={isFavorite}
              >
                <FiHeart className={isFavorite ? "fill-current text-red-500" : ""} />
                <span>Favoris</span>
              </button>
            </div>

            <div className="mt-5 rounded-xl border bg-white p-5">
              <div className="flex items-end gap-3">
                <div className="text-3xl md:text-4xl font-extrabold text-primary-600">
                  {currencyFmt(Number(deal.discountedPrice) || 0)}
                </div>
                <div className="text-lg text-gray-500 line-through">{currencyFmt(Number(deal.originalPrice) || 0)}</div>
              </div>

              <div className="mt-1 text-sm font-semibold text-green-600">
                Vous économisez{" "}
                {currencyFmt((Number(deal.originalPrice) || 0) - (Number(deal.discountedPrice) || 0))}
              </div>

              {/* ✅ QUANTITÉ - Visible seulement si connecté */}
              {user && (
                <div className="mt-5">
                  <div className="text-sm font-medium text-gray-700 mb-2">Quantité</div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50"
                      aria-label="Diminuer la quantité"
                    >
                      -
                    </button>
                    <div className="w-12 text-center text-lg font-semibold">{quantity}</div>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(deal.quantityRemaining || 1, q + 1))}
                      className="w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50"
                      aria-label="Augmenter la quantité"
                    >
                      +
                    </button>
                    <div className="text-sm text-gray-600">{deal.quantityRemaining ?? 0} disponibles</div>
                  </div>
                </div>
              )}

              {/* ✅ BOUTONS D'ACTION */}
              <div className="mt-5 space-y-3">
                {/* ✅ WHATSAPP - TOUJOURS VISIBLE */}
                {companyWhatsapp && (
                  <a
                    href={`https://wa.me/${companyWhatsapp}?text=${whatsappText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <FaWhatsapp size={20} />
                    <span>Commander via WhatsApp</span>
                  </a>
                )}

                {/* ✅ SI CONNECTÉ: boutons achat */}
                {user ? (
                  <>
                    <button
                      type="button"
                      onClick={handleBuyNow}
                      disabled={(deal.quantityRemaining ?? 0) === 0}
                      className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Acheter maintenant
                    </button>

                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={(deal.quantityRemaining ?? 0) === 0}
                      className="w-full border-2 border-primary-600 text-primary-600 py-3 rounded-lg font-semibold hover:bg-primary-50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <FiShoppingCart />
                      <span>Ajouter au panier</span>
                    </button>
                  </>
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-5 text-center">
                    <FiLock className="mx-auto text-gray-400 mb-3" size={32} />
                    <p className="text-gray-700 font-medium mb-3">Connectez-vous pour acheter en ligne</p>
                    <Link
                      to="/login"
                      className="inline-block bg-primary-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-700 transition"
                    >
                      Se connecter
                    </Link>
                    <p className="text-xs text-gray-500 mt-3">
                      Pas encore de compte?{" "}
                      <Link to="/register" className="text-primary-600 hover:underline font-medium">
                        S'inscrire
                      </Link>
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-5 border-t pt-4 space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <FiClock />
                  <span>
                    Se termine dans {daysRemaining} jour{daysRemaining > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FiMapPin />
                  <span className="truncate">{locations.join(", ") || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* REST OF THE PAGE */}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold mb-3">Description</h2>
            <div className="bg-white border rounded-xl p-5 text-gray-700 leading-relaxed">
              {deal.description || "Aucune description fournie."}
            </div>

            {Array.isArray(deal.highlights) && deal.highlights.length > 0 && (
              <div className="mt-6 bg-white border rounded-xl p-5">
                <h3 className="text-lg font-bold mb-3">Points forts</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  {deal.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(deal.conditions) && deal.conditions.length > 0 && (
              <div className="mt-6 bg-white border rounded-xl p-5">
                <h3 className="text-lg font-bold mb-3">Conditions</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  {deal.conditions.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-xl font-bold mb-3">Avis des clients</h3>

              <div className="mb-6 bg-white border rounded-xl p-5">
                <div className="font-semibold mb-2">Laisser un avis</div>
                {user ? (
                  <form onSubmit={handleSubmitReview}>
                    {renderStarsInput()}
                    <textarea
                      className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder="Partagez votre expérience..."
                      value={myComment}
                      onChange={(e) => setMyComment(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
                    >
                      Envoyer mon avis
                    </button>
                  </form>
                ) : (
                  <p className="text-sm text-gray-600">
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="text-primary-600 underline font-semibold"
                    >
                      Connectez-vous
                    </button>{" "}
                    pour laisser un avis.
                  </p>
                )}
              </div>

              {reviewsLoading ? (
                <div className="text-gray-600">Chargement des avis...</div>
              ) : reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((r) => (
                    <div key={r._id} className="bg-white border rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-gray-900">
                          {r.userId?.firstName} {r.userId?.lastName}
                        </div>
                        {renderStarsReadOnly(r.rating)}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {new Date(r.createdAt).toLocaleDateString("fr-FR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric"
                        })}
                      </div>
                      <div className="mt-2 text-gray-700">{r.comment}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500">Aucun avis pour le moment.</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-xl border bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">À propos de la société</div>

                <div className="mt-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-700 text-sm">
                    {(companyName || "M").charAt(0).toUpperCase()}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-gray-900 truncate text-sm">{companyName}</div>

                    {companyAddress && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiMapPin className="inline -mt-0.5 mr-1" />
                        {companyAddress}
                      </div>
                    )}

                    {companyPhone && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiPhone className="inline -mt-0.5 mr-1" />
                        {companyPhone}
                      </div>
                    )}

                    {companyEmail && (
                      <div className="mt-1 text-xs text-gray-600">
                        <FiMail className="inline -mt-0.5 mr-1" />
                        {companyEmail}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {companyPhone ? (
                    <a
                      href={`tel:${companyPhone}`}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </a>
                  ) : companyEmail ? (
                    <a
                      href={`mailto:${companyEmail}`}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toast.info("Contact indisponible")}
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 text-white font-semibold py-2 text-sm hover:bg-primary-700"
                    >
                      Contacter
                    </button>
                  )}

                  {companyWhatsapp && (
                    <a
                      href={`https://wa.me/${companyWhatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center rounded-lg bg-green-500 text-white font-semibold py-2 text-sm hover:bg-green-600"
                    >
                      <FaWhatsapp className="mr-2" />
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {isLightboxOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white bg-black/60 rounded-full p-2 hover:bg-black"
            aria-label="Fermer l'image"
          >
            <FiX size={20} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goPrevImage();
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg"
                aria-label="Image précédente"
              >
                <FiChevronLeft size={26} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goNextImage();
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-3 shadow-lg"
                aria-label="Image suivante"
              >
                <FiChevronRight size={26} />
              </button>
            </>
          )}

          <img
            src={images[selectedImage]}
            alt={deal.title || "Deal"}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}
    </>
  );
};

export default DealDetail;
